import { createFileRoute } from '@tanstack/react-router';
import { Container, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@apollo/client';
import { myCampaignsDocument } from '@social-garden/api/documents/campaign.ts';
import { myCreatorHasCampaignsForManagerDocument } from '@social-garden/api/documents/creatorHasCampaign.ts';
import CampaignListItem from '@/components/CampaignListItem.tsx';
import PageTitle from '@/components/PageTitle.tsx';
import CreatorHasCampaignListItem from '@/components/CreatorHasCampaignListItem.tsx';
import DashboardEmpty from '@/components/Dashboard/DashboardEmpty.tsx';
import ButtonLink from '@/components/ButtonLink.tsx';

export const Route = createFileRoute('/_manager/_app/')({
  component: Dashboard,
});

const CAMPAIGN_ITEM_SIZE = 6;
const CREATOR_HAS_CAMPAIGN_ITEM_SIZE = 6;

function Dashboard() {
  const { t } = useTranslation(['manager']);

  const {
    data: { myCampaigns },
  } = useSuspenseQuery(myCampaignsDocument, {
    variables: {
      input: {
        brandIds: [],
        status: [],
      },
      first: CAMPAIGN_ITEM_SIZE,
      page: 1,
    },
  });

  const {
    data: { myCreatorHasCampaigns },
  } = useSuspenseQuery(myCreatorHasCampaignsForManagerDocument, {
    variables: {
      input: {
        searchName: undefined,
        status: [],
      },
      first: CREATOR_HAS_CAMPAIGN_ITEM_SIZE,
      page: 1,
    },
  });

  if (
    myCampaigns.data.length === 0 &&
    myCreatorHasCampaigns.data.length === 0
  ) {
    return <DashboardEmpty />;
  }

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid size={12}>
          <PageTitle>{t('manager:navigation.dashboard')}</PageTitle>
        </Grid>
        {myCampaigns.data.length > 0 ? (
          <>
            <Grid container alignItems="center" size={12}>
              <Grid
                size={{
                  xs: 12,
                  md: 'grow',
                }}>
                <Typography variant="h4">
                  {t('dashboard.latestCampaignsHeader')}
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 'auto',
                }}>
                <ButtonLink
                  to="/campaigns/create"
                  size="large"
                  variant="contained">
                  {t('dashboard.createCampaignButton')}
                </ButtonLink>
              </Grid>
            </Grid>
            {myCampaigns.data.slice(0, CAMPAIGN_ITEM_SIZE).map((campaign) => (
              <Grid
                key={campaign.id}
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                  lg: 6,
                  xl: 4,
                }}>
                <CampaignListItem data={campaign} variant="outlined" />
              </Grid>
            ))}
            <Grid textAlign="center" size={12}>
              <ButtonLink variant="text" to="/campaigns">
                {t('dashboard.allCampaignsButton')}
              </ButtonLink>
            </Grid>
          </>
        ) : null}
        {myCreatorHasCampaigns.data.length > 0 ? (
          <>
            <Grid size={12}>
              <Typography variant="h4">
                {t('dashboard.latestCollaborationsHeader')}
              </Typography>
            </Grid>
            {myCreatorHasCampaigns.data
              .slice(0, CREATOR_HAS_CAMPAIGN_ITEM_SIZE)
              .map((creatorHasCampaign) => (
                <Grid key={creatorHasCampaign.id} size={12}>
                  <CreatorHasCampaignListItem
                    data={creatorHasCampaign}
                    variant="outlined"
                  />
                </Grid>
              ))}
            <Grid textAlign="center" size={12}>
              <ButtonLink variant="text" to="/collaborations">
                {t('dashboard.allCollaborationsButton')}
              </ButtonLink>
            </Grid>
          </>
        ) : null}
      </Grid>
    </Container>
  );
}
